/*
  Les groupes d'une journée pédagogique (demandé par Éclosion le 03/11/2026).

  Le jour même, l'équipe ne garde pas les classes : elle forme ses groupes —
  par exemple M1A (14) et M1B (11) ensemble, « Les M1 », 25 enfants — et les
  ajuste : fusionner deux petits groupes, scinder un grand, y compris en
  séparant une classe, déplacer un enfant. Le taux d'encadrement reste à
  l'équipe : rien ici ne le calcule.

  Les groupes sont les sous-groupes des stages (`subgroups`, et
  `registrations.subgroup_id` pour chaque inscription) : une journée est une
  session comme une autre. Les écrans des stages écartent les journées
  (JPA13) ; ces groupes-là ne s'y montrent donc pas.

  Ce fichier ne lit ni n'écrit rien : il propose, et l'écran applique.
*/

import type { InscritJpa } from './regles';

/** L'année d'une classe : « M1 », « P3 »… et de quoi les ranger. */
export interface AnneeDeClasse {
  cle: string;
  ordre: number;
  niveau: 'maternelle' | 'primaire';
}

/**
 * L'année d'une classe d'après son libellé : « M1A - Madame Julie » → M1,
 * « P3C » → P3, « 2e primaire A — Madame Claire » → P2, « Accueil » → M0.
 * `null` quand le libellé ne dit pas l'année : l'enfant se range alors avec
 * sa classe, telle quelle.
 */
export function anneeDeClasse(libelle: string | null | undefined): AnneeDeClasse | null {
  const t = (libelle ?? '').trim();
  const court = /^([MP])\s*(\d)/i.exec(t);
  if (court) {
    const niveau = court[1].toUpperCase() === 'M' ? 'maternelle' : 'primaire';
    const n = Number(court[2]);
    return { cle: `${niveau === 'maternelle' ? 'M' : 'P'}${n}`, ordre: (niveau === 'maternelle' ? 0 : 10) + n, niveau };
  }
  const long = /^(\d)\s*(?:re|er|e|ère|ème|ere|eme)?\s+(maternelle|primaire)/i.exec(t);
  if (long) {
    const niveau = long[2].toLowerCase() === 'maternelle' ? 'maternelle' : 'primaire';
    const n = Number(long[1]);
    return { cle: `${niveau === 'maternelle' ? 'M' : 'P'}${n}`, ordre: (niveau === 'maternelle' ? 0 : 10) + n, niveau };
  }
  if (/^accueil/i.test(t)) return { cle: 'M0', ordre: 0, niveau: 'maternelle' };
  return null;
}

/** La classe courte d'un libellé, pour une colonne étroite : « M1A - Madame Julie » → « M1A ». */
export function classeCourte(libelle: string | null | undefined): string {
  const t = (libelle ?? '').trim();
  if (!t) return '—';
  const court = /^([MP]\s*\d\s*[A-Z]?)\b/i.exec(t);
  if (court) return court[1].replace(/\s+/g, '').toUpperCase();
  return t.split(/\s+[-—–]\s+/)[0];
}

/** Un groupe proposé : son nom et les inscriptions qu'il reçoit, dans l'ordre où l'écran les crée. */
export interface GroupePropose {
  nom: string;
  inscriptionIds: string[];
}

const SANS_CLASSE = 'Sans classe indiquée';

/** La clé de rangement d'un enfant : son année, sinon sa classe telle quelle, sinon « sans classe ». */
function cleDeRangement(i: Pick<InscritJpa, 'classe'>): { cle: string; nom: string; ordre: number } {
  const annee = anneeDeClasse(i.classe);
  if (annee) return { cle: annee.cle, nom: `Les ${annee.cle}`, ordre: annee.ordre };
  if (i.classe?.trim()) return { cle: `classe:${i.classe.trim()}`, nom: i.classe.trim(), ordre: 100 };
  return { cle: 'sans', nom: SANS_CLASSE, ordre: 1000 };
}

/**
 * Un groupe par année : toutes les M1 ensemble, toutes les P2 ensemble.
 * Les années dans l'ordre (M0, M1… puis P1…), puis les classes dont le nom ne
 * dit pas l'année, puis les enfants sans classe.
 */
export function proposerParAnnee(inscrits: Pick<InscritJpa, 'registrationId' | 'classe' | 'nom' | 'prenom'>[]): GroupePropose[] {
  const parCle = new Map<string, { nom: string; ordre: number; ids: string[] }>();
  for (const i of trierParNom(inscrits)) {
    const r = cleDeRangement(i);
    const g = parCle.get(r.cle) ?? { nom: r.nom, ordre: r.ordre, ids: [] };
    g.ids.push(i.registrationId);
    parCle.set(r.cle, g);
  }
  return [...parCle.values()]
    .sort((a, b) => a.ordre - b.ordre || a.nom.localeCompare(b.nom, 'fr'))
    .map((g) => ({ nom: g.nom, inscriptionIds: g.ids }));
}

/**
 * Où placer un enfant arrivé après la répartition : le groupe où sont le plus
 * d'enfants de sa classe ; à défaut, de son année. `null` : aucun indice, il
 * reste à répartir à la main.
 */
export function groupeProposePour(
  enfant: Pick<InscritJpa, 'registrationId' | 'classeId' | 'classe'>,
  inscrits: Pick<InscritJpa, 'registrationId' | 'classeId' | 'classe'>[],
  groupeDe: (registrationId: string) => string | null,
): string | null {
  const majoritaire = (memes: (i: Pick<InscritJpa, 'classeId' | 'classe'>) => boolean): string | null => {
    const comptes = new Map<string, number>();
    for (const i of inscrits) {
      if (i.registrationId === enfant.registrationId || !memes(i)) continue;
      const g = groupeDe(i.registrationId);
      if (g) comptes.set(g, (comptes.get(g) ?? 0) + 1);
    }
    let meilleur: string | null = null;
    let n = 0;
    for (const [g, c] of comptes) if (c > n) { meilleur = g; n = c; }
    return meilleur;
  };
  if (enfant.classeId) {
    const parClasse = majoritaire((i) => i.classeId === enfant.classeId);
    if (parClasse) return parClasse;
  }
  const annee = anneeDeClasse(enfant.classe)?.cle;
  if (annee) return majoritaire((i) => anneeDeClasse(i.classe)?.cle === annee);
  return null;
}

/**
 * Scinder un groupe en deux moitiés proches.
 *
 * Plusieurs classes : elles restent entières quand c'est assez équilibré (la
 * plus grande d'abord, dans la moitié la plus légère) — M1A 14 et M1B 11
 * donnent 14 et 11. Sinon, et pour une seule classe, les enfants en ligne,
 * classe après classe et par nom, coupés au milieu : une classe au plus est
 * séparée — M1A 20 et M1B 2 donnent 11 et 11. L'équipe ajuste ensuite enfant
 * par enfant.
 */
export function scinderEnDeux(
  inscrits: Pick<InscritJpa, 'registrationId' | 'classeId' | 'classe' | 'classeOrdre' | 'nom' | 'prenom'>[],
): [string[], string[]] {
  const parClasse = new Map<string, { ordre: number; libelle: string; ids: string[] }>();
  for (const i of trierParNom(inscrits)) {
    const cle = i.classeId ?? 'sans';
    const c = parClasse.get(cle) ?? { ordre: i.classeId ? (i.classeOrdre ?? 999) : 1000, libelle: i.classe ?? '', ids: [] };
    c.ids.push(i.registrationId);
    parClasse.set(cle, c);
  }
  const n = inscrits.length;
  if (parClasse.size >= 2) {
    const moities: [string[], string[]] = [[], []];
    for (const c of [...parClasse.values()].sort((a, b) => b.ids.length - a.ids.length)) {
      moities[moities[0].length <= moities[1].length ? 0 : 1].push(...c.ids);
    }
    if (Math.abs(moities[0].length - moities[1].length) <= Math.max(2, Math.floor(n / 4))) return moities;
  }
  const enLigne = [...parClasse.values()]
    .sort((a, b) => a.ordre - b.ordre || a.libelle.localeCompare(b.libelle, 'fr'))
    .flatMap((c) => c.ids);
  const moitie = Math.ceil(n / 2);
  return [enLigne.slice(0, moitie), enLigne.slice(moitie)];
}

/** Le nom de la seconde moitié : « Les M1 » → « Les M1 2 » ; « Les M1 2 » → « Les M1 3 ». */
export function nomDeLaMoitie(nom: string, pris: string[]): string {
  return nomLibre(nom.replace(/\s+\d+$/, '') || nom, pris);
}

/**
 * Les groupes dans l'ordre de leur nom, les nombres lus comme des nombres :
 * « Les M1 », « Les M1 2 », « Les M2 »… « Les P3 ». Un ordre qui ne bouge pas
 * quand on déplace des enfants, et qui range une moitié à côté de l'autre.
 */
export function trierGroupes<T extends { nom: string }>(groupes: T[]): T[] {
  return [...groupes].sort((a, b) => a.nom.localeCompare(b.nom, 'fr', { numeric: true, sensitivity: 'base' }));
}

/**
 * Les groupes d'un autre jour, repris pour celui-ci (le 6 comme le 5) : le
 * même enfant retrouve son groupe ; un autre rejoint, ce jour-là, le groupe de
 * sa classe, à défaut celui de son année ; sans indice, il reste à répartir.
 * Les groupes que personne ne rejoint ne sont pas repris.
 */
export function reprendreLesGroupes(
  inscrits: Pick<InscritJpa, 'registrationId' | 'kidId' | 'classeId' | 'classe' | 'nom' | 'prenom'>[],
  autreJour: {
    inscrits: Pick<InscritJpa, 'registrationId' | 'kidId' | 'classeId' | 'classe'>[];
    groupes: { id: string; nom: string }[];
    groupeDe: (registrationId: string) => string | null;
  },
): GroupePropose[] {
  const parEnfant = new Map(autreJour.inscrits.map((i) => [i.kidId, autreJour.groupeDe(i.registrationId)]));
  const proposes = new Map(autreJour.groupes.map((g) => [g.id, { nom: g.nom, inscriptionIds: [] as string[] }]));
  for (const i of trierParNom(inscrits)) {
    const g = parEnfant.get(i.kidId) ?? groupeProposePour(i, autreJour.inscrits, autreJour.groupeDe);
    if (g) proposes.get(g)?.inscriptionIds.push(i.registrationId);
  }
  return [...proposes.values()].filter((g) => g.inscriptionIds.length > 0);
}

/** Ce que l'en-tête d'un groupe dit de ses classes : « M1A 14 · M1B 11 », dans l'ordre des classes. */
export function classesDuGroupe(
  inscrits: Pick<InscritJpa, 'classeId' | 'classe' | 'classeOrdre'>[],
): { classeId: string | null; libelle: string; nombre: number }[] {
  const parClasse = new Map<string, { classeId: string | null; libelle: string; ordre: number; nombre: number }>();
  for (const i of inscrits) {
    const cle = i.classeId ?? 'sans';
    const c = parClasse.get(cle)
      ?? { classeId: i.classeId, libelle: i.classe ? classeCourte(i.classe) : 'Sans classe', ordre: i.classeOrdre ?? 1000, nombre: 0 };
    c.nombre += 1;
    parClasse.set(cle, c);
  }
  return [...parClasse.values()]
    .sort((a, b) => a.ordre - b.ordre || a.libelle.localeCompare(b.libelle, 'fr'))
    .map(({ classeId, libelle, nombre }) => ({ classeId, libelle, nombre }));
}

/** Un nom libre pour un nouveau groupe : « Les M1 2 », puis « Les M1 3 »… */
export function nomLibre(base: string, pris: string[]): string {
  const cles = new Set(pris.map((n) => n.trim().toLocaleLowerCase('fr')));
  for (let n = 2; ; n++) {
    const nom = `${base} ${n}`;
    if (!cles.has(nom.toLocaleLowerCase('fr'))) return nom;
  }
}

export function trierParNom<T extends Pick<InscritJpa, 'nom' | 'prenom'>>(inscrits: T[]): T[] {
  return [...inscrits].sort((a, b) =>
    a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' }) || a.prenom.localeCompare(b.prenom, 'fr', { sensitivity: 'base' }));
}
